import { axios } from '@/utils/axios';
import type {
  ExternalAuthCallbackResponse,
  ExternalIdentitySummary,
  ExternalProviderDescriptor,
  ExternalRegistrationRequest,
} from '@/types/externalAuth';

export const getExternalProviders = () =>
  axios.get<{ success: boolean; data: { providers: ExternalProviderDescriptor[] }; message: string }>(
    '/api/auth/external/providers'
  );

export const startExternalAuth = (
  provider: string,
  purpose: 'login' | 'register',
  returnPath?: string | null,
  turnstileToken?: string | null
) => axios.post<{ success: boolean; data: { authorization_url: string; expires_at: string }; message: string }>(
  `/api/auth/external/${encodeURIComponent(provider)}/start`,
  {
    purpose,
    ...(returnPath ? { return_path: returnPath } : {}),
    ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
  }
);

export const completeExternalAuth = (provider: string, data: Record<string, string>) =>
  axios.post<ExternalAuthCallbackResponse>(`/api/auth/external/${encodeURIComponent(provider)}/callback`, data);

export const completeExternalRegistration = (data: ExternalRegistrationRequest) =>
  axios.post<MemberSignInResponse>('/api/auth/external/registration/complete', data);

export const startExternalRecovery = (provider: string, recoveryToken: string) =>
  axios.post<{ success: boolean; data: { authorization_url: string; expires_at: string } }>(
    `/api/auth/external/recovery/${encodeURIComponent(provider)}/start`,
    { recovery_token: recoveryToken }
  );

export const getExternalIdentities = () =>
  axios.get<{ success: boolean; data: { identities: ExternalIdentitySummary[]; providers: ExternalProviderDescriptor[] } }>(
    '/api/profile/security/external-identities'
  );

export const startExternalIdentityLink = (
  provider: string,
  proof: { reauth_intent_id: string } | { challenge_id: string; code: string }
) => axios.post<{ success: boolean; data: { authorization_url: string } }>(
  `/api/profile/security/external-identities/${encodeURIComponent(provider)}/start`,
  { ...proof, return_path: '/profile/security' }
);

export const startExternalReauth = (provider: string, action: string) =>
  axios.post<{ success: boolean; data: { authorization_url: string } }>(
    `/api/profile/security/external-reauth/${encodeURIComponent(provider)}/start`,
    { action, return_path: '/profile/security' }
  );

export const unlinkExternalIdentity = (provider: string, reauthIntentId: string) =>
  axios.delete(`/api/profile/security/external-identities/${encodeURIComponent(provider)}`, {
    data: { reauth_intent_id: reauthIntentId },
  });

export const linkPendingExternalIdentity = (intentId: string) =>
  axios.post('/api/profile/security/external-identities/link-pending', { intent_id: intentId });

export const createExternalSubaccountInvitation = () =>
  axios.post<{ success: boolean; data: { invitation_id: string; invitation_token: string; expires_at: string } }>(
    '/api/external-subaccount-invitations'
  );

export const revokeExternalSubaccountInvitation = (id: string) =>
  axios.delete(`/api/external-subaccount-invitations/${encodeURIComponent(id)}`);

export const inspectExternalSubaccountInvitation = (invitationToken: string) =>
  axios.post<{ success: boolean; data: { invitation_id: string; company_name: string; expires_at: string } }>(
    '/api/external-subaccount-invitations/inspect',
    { invitation_token: invitationToken }
  );
